import React from "react";

import { Info, Medal, Users, Zap } from "lucide-react";

import CompositeDonut from "./CompositeDonut";
import { TierPill } from "./EngagementBadges";
import {
  MCI_SAMPLE_MESSAGES,
  SAMPLE_MY_PERFORMANCE,
} from "./MyCheckInsSampleState";
import PerformanceAccountBreakdown from "./PerformanceAccountBreakdown";
import { HubPageLoading } from "../../Components/EngagementEmptyPanel";
import { SampleDataOverlay } from "../../Container/DiscoveryHub/DiscoveryHubSampleState";
import {
  MCI_MUTED,
  notificationBannerClass,
} from "../../Utils/Helper/sponsorEngagementUi";

const performanceSampleMessagePending = (period: string) =>
  `Preview of My Performance — ${period} results are pending publication. Scores will appear here once the leaderboard is published.`;

const RANK_PENDING_TOOLTIP = "Rank will show after the leaderboard published";

type MyCheckInsPerformancePanelProps = {
  data: any | null;
  loading?: boolean;
  /** When false, skip sample fallback (used when rendering sample payload itself). */
  allowSampleFallback?: boolean;
};

const StatItem = ({
  icon: Icon,
  label,
  value,
  infoTooltip,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  infoTooltip?: string;
}) => (
  <div className="flex items-start gap-2.5">
    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100">
      <Icon size={13} className="text-gray-500" />
    </div>
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
        <span>{label}</span>
        {infoTooltip ? (
          <span
            className="relative inline-flex group"
            tabIndex={0}
            aria-label={infoTooltip}
          >
            <Info
              size={12}
              strokeWidth={2}
              className="text-gray-400 group-hover:text-gray-500 group-focus-within:text-gray-500"
              aria-hidden
            />
            <span
              role="tooltip"
              className="pointer-events-none absolute left-full top-1/2 z-20 ml-1.5 w-max max-w-[220px] -translate-y-1/2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-[11px] font-medium normal-case tracking-normal text-gray-700 opacity-0 shadow-md transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
            >
              {infoTooltip}
            </span>
          </span>
        ) : null}
      </div>
      <div className="mt-0.5 text-[13.5px] font-bold text-gray-900">
        {value}
      </div>
    </div>
  </div>
);

const RankValue = ({
  published,
  rank,
  totalSponsors,
}: {
  published: boolean;
  rank?: number | null;
  totalSponsors?: number | null;
}) => {
  const total =
    totalSponsors != null && totalSponsors > 0 ? (
      <span className={`text-[11px] font-normal ${MCI_MUTED} ml-0.5`}>
        / {totalSponsors}
      </span>
    ) : null;

  if (published && rank != null) {
    return (
      <span>
        #{rank}
        {total}
      </span>
    );
  }

  return <span>—{total}</span>;
};

const hasUsablePerformanceData = (data: any | null): boolean =>
  Boolean(data?.cycle) && (data?.accounts?.length ?? 0) > 0;

const MyCheckInsPerformancePanel: React.FC<MyCheckInsPerformancePanelProps> = ({
  data,
  loading = false,
  allowSampleFallback = true,
}) => {
  if (loading) {
    return <HubPageLoading message="Loading performance…" />;
  }

  const displayPeriodHint = data?.display_period ?? data?.period;
  const isCurrentPublished = Boolean(data?.is_current_published);
  const pubState = data?.cycle?.publication_state;

  const pendingBanner =
    data && !isCurrentPublished && data.period ? (
      <div className={notificationBannerClass("amber")}>
        <div className="min-w-0">
          <div className="font-bold text-[13px] mb-px">
            {data.period} results pending publication
          </div>
          <div className="text-xs opacity-90">
            Showing {displayPeriodHint} (most recently published). New
            cycle&apos;s draft is{" "}
            {pubState === "draft"
              ? "still being prepared by ESL"
              : "in CCO review"}
            .
          </div>
        </div>
      </div>
    ) : null;

  if (allowSampleFallback && !hasUsablePerformanceData(data)) {
    const sampleMessage = pendingBanner
      ? performanceSampleMessagePending(String(data.period))
      : MCI_SAMPLE_MESSAGES.performance;
    return (
      <>
        {pendingBanner}
        <SampleDataOverlay message={sampleMessage}>
          <MyCheckInsPerformancePanel
            data={SAMPLE_MY_PERFORMANCE}
            allowSampleFallback={false}
          />
        </SampleDataOverlay>
      </>
    );
  }

  const summary = data?.summary ?? {};
  const accounts = data?.accounts ?? [];
  const displayPeriod = data?.display_period ?? data?.period;
  const polls =
    data?.polls ??
    (data?.cycle
      ? [
          {
            cycle_id: data.cycle?.id ?? "default",
            poll_name: displayPeriod,
            period: displayPeriod,
          },
        ]
      : []);
  const weights = data?.leaderboard_weights ?? {};
  const streak = summary.streak ?? 0;
  const streakThreshold = summary.streak_threshold ?? 3;
  const hasStreak = streak >= streakThreshold;

  return (
    <>
      {allowSampleFallback ? pendingBanner : null}

      <div className="grid grid-cols-[340px_1fr] gap-5 max-[960px]:grid-cols-1">
        {/* ── Score card ── */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          {/* Card header */}
          <div className="py-3.5 px-4 flex items-center justify-between gap-2 border-b border-gray-200">
            <h3 className="text-[13.5px] font-bold text-gray-800">
              My Performance
            </h3>
            <span className="text-[11px] font-normal text-gray-400">
              {displayPeriod}
            </span>
          </div>

          <div className="flex flex-col px-4 pb-4">
            {/* Rainbow gauge ring */}
            <div className="mt-4 flex justify-center">
              <CompositeDonut
                composite={summary.portfolio_composite}
                periodLabel="Composite Score"
                tier={summary.portfolio_tier}
              />
            </div>

            {/* Tier pill */}
            <div className="mt-2 flex justify-center">
              <TierPill
                tier={summary.portfolio_tier}
                className="!text-[13px] !px-3.5 !py-1"
              />
            </div>

            {/* Top highlights */}
            <div className="mt-5 border-t border-gray-100 pt-4">
              <p className="mb-3.5 text-[13px] font-bold text-gray-800">
                Top highlights <span>{hasStreak ? "🔥" : "⭐"}</span>
              </p>

              <div className="grid grid-cols-2 gap-x-4 gap-y-3.5">
                <StatItem
                  icon={Medal}
                  label="Rank"
                  infoTooltip={
                    isCurrentPublished ? undefined : RANK_PENDING_TOOLTIP
                  }
                  value={
                    <RankValue
                      published={isCurrentPublished}
                      rank={summary.rank}
                      totalSponsors={summary.total_sponsors}
                    />
                  }
                />
                <StatItem
                  icon={Users}
                  label="Accounts"
                  value={summary.account_count ?? accounts.length ?? "—"}
                />
                {hasStreak ? (
                  <StatItem
                    icon={Zap}
                    label="Streak"
                    value={`${streak} qtrs`}
                  />
                ) : null}
              </div>
            </div>

            {/* Score methodology expandable */}
            <details className="mt-5 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-left">
              <summary className="flex cursor-pointer list-none items-center gap-1.5 text-xs font-medium text-gray-500 [&::-webkit-details-marker]:hidden before:content-['▶'] before:text-[8px] before:text-gray-400 open:before:rotate-90">
                How is my score calculated?
              </summary>
              <div className="mt-2 border-t border-dashed border-gray-200 pt-2 text-xs leading-relaxed text-gray-600">
                Portfolio composite = average of account composites. Each
                account composite is the weighted average of Poll 1 and Poll 2:
                <div className="mt-2 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-700">
                      Team Feedback (Poll 2)
                    </span>
                    <span>{((weights.team ?? 0) * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-700">
                      Engagement Activity (Poll 1)
                    </span>
                    <span>{((weights.activity ?? 0) * 100).toFixed(0)}%</span>
                  </div>
                </div>
                <p className={`mt-2 text-[11px] ${MCI_MUTED}`}>
                  Tiers are re-derived automatically when weights change.
                </p>
              </div>
            </details>
          </div>
        </div>

        {/* ── Account breakdown ── */}
        <div className="flex min-w-0 flex-col h-full">
          <PerformanceAccountBreakdown
            polls={polls}
            accounts={accounts}
            displayPeriod={displayPeriod}
          />
        </div>
      </div>
    </>
  );
};

export default MyCheckInsPerformancePanel;
